import {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
  type KeyboardEvent,
  type SyntheticEvent,
} from "react";
import { toast } from "react-toastify";
import { announce } from "../../../lib/announce";
import { MacrosContext } from "../../../utils/MacrosContext";
import { usePatientChatMessage, usePatientChatSession } from "../../../hooks/usePatientChat";
import { useDeepgramDictation } from "../../../hooks/useDeepgramDictation";
import type {
  PatientChatArtifact,
  PatientChatConversationTurn,
  PatientChatRequestKind,
  PatientChatSessionResponse,
  PatientChatUploadedFile,
} from "../../../lib/patientChat";
import { extractPromptReformatStreamPreview } from "../../../lib/promptReformatStream";
import {
  insertNoteDictationText,
  rebaseNoteDictationInterimRange,
  syncNoteDictationAfterEdit,
  type NoteDictationInterimRange,
} from "../../noteList/noteDictation";
import {
  resolvePatientChatDraftOnHydrate,
  shouldHydratePatientChatSession,
} from "../../noteList/patientChatHydration";
import {
  conversationHasIntervalHistoryDraft,
  EMPTY_SMART_ASSIST_PROMPT_MESSAGE,
  PROMPT_WORKFLOW_STEPS,
  SMART_ASSIST_FILE_LIMIT_MESSAGE,
} from "../../noteList/SmartAssistPromptTools";
import {
  ATTACHMENT_LIMIT,
  buildUploadedFile,
  getAttachmentSizeError,
  getUploadedFileKey,
  hasUploadPayload,
} from "./attachments";
import { buildDraftContext } from "./draftContext";
import type {
  PromptWorkflowStep,
  SmartAssistCitations,
  SmartAssistLocalState,
  SmartAssistQuickAction,
  UseSmartAssistOptions,
  UseSmartAssistResult,
} from "./types";

export const SMART_ASSIST_REQUEST_FAILED_MESSAGE =
  "Smart Assist couldn't complete that request. Please try again.";
export const SMART_ASSIST_READING_CHART_STATUS = "Reading chart…";
export const SMART_ASSIST_THINKING_STATUS = "Thinking…";
const DEFAULT_CONFLICTING_DICTATION_MESSAGE =
  "Stop the other dictation before starting Smart Assist dictation.";
const NO_ATTACHMENT_MESSAGE = "Attach at least one file to summarize.";
const UPLOADED_FILES_FALLBACK_PROMPT =
  "Summarize the uploaded objective data files and any relevant chart history.";

interface MacrosContextValue {
  userMacros?: Array<{ dot_phrase?: string; content?: string; value?: string }>;
  fetchUserMacros?: () => unknown;
}

interface TextSelection {
  start: number;
  end: number;
}

const EMPTY_LOCAL_STATE: SmartAssistLocalState = {
  conversation: [],
  draftPrompt: "",
  latestReply: null,
  latestCitations: {},
  latestRequestKind: null,
  latestRunId: null,
  latestArtifacts: [],
  promptWorkflowStep: PROMPT_WORKFLOW_STEPS.IDLE,
  activeQuickAction: null,
  replyOriginQuickActionId: null,
  uploadedFiles: [],
};

const getSessionStamp = (session: PatientChatSessionResponse) =>
  `${session.sessionId}:${session.updatedAt}`;

const getErrorMessage = (error: unknown, fallback: string) =>
  error instanceof Error && error.message ? error.message : fallback;

const isFileDragEvent = (event: DragEvent<HTMLElement>) =>
  Array.from(event.dataTransfer?.types || []).includes("Files") ||
  (event.dataTransfer?.files?.length || 0) > 0;

export function useSmartAssist({
  patientId,
  capabilities,
  draftSaver,
  note = "",
  patientSummary,
  diarizedTranscript,
  noteCitations,
  chartHistory,
  includeFullChartHistory = false,
  sendEmptyContext = false,
  stateCacheRef,
  dotPhraseMacrosByPhrase,
  isConflictingDictationActive = false,
  conflictingDictationMessage = DEFAULT_CONFLICTING_DICTATION_MESSAGE,
  onDictationStateChange,
  queuedPrompt = "",
  onQueuedPromptConsumed,
  isHidden = false,
}: UseSmartAssistOptions): UseSmartAssistResult {
  const { userMacros, fetchUserMacros } = (useContext(MacrosContext) || {}) as MacrosContextValue;
  const { saveDraft, markDraftSaved, clearSession, getLastWrittenVersion, hasPendingWrite } =
    draftSaver;

  const cachedState =
    stateCacheRef?.current?.patientId === patientId ? stateCacheRef.current : null;
  const [prompt, setPrompt] = useState(cachedState?.draftPrompt || "");
  const [conversation, setConversation] = useState<PatientChatConversationTurn[]>(
    cachedState?.conversation || []
  );
  const [reply, setReply] = useState(cachedState?.latestReply || "");
  const [citations, setCitations] = useState<SmartAssistCitations>(
    cachedState?.latestCitations || {}
  );
  const [requestKind, setRequestKind] = useState<PatientChatRequestKind | null>(
    cachedState?.latestRequestKind || null
  );
  const [latestRunId, setLatestRunId] = useState<string | null>(cachedState?.latestRunId || null);
  const [artifacts, setArtifacts] = useState<PatientChatArtifact[]>(
    cachedState?.latestArtifacts || []
  );
  const [promptWorkflowStep, setPromptWorkflowStep] = useState<PromptWorkflowStep>(
    cachedState?.promptWorkflowStep || PROMPT_WORKFLOW_STEPS.IDLE
  );
  const [activeQuickAction, setActiveQuickAction] = useState<SmartAssistQuickAction | null>(
    cachedState?.activeQuickAction || null
  );
  const [replyOriginQuickActionId, setReplyOriginQuickActionId] = useState<string | null>(
    cachedState?.replyOriginQuickActionId || null
  );
  const [uploadedFiles, setUploadedFiles] = useState<PatientChatUploadedFile[]>(
    (cachedState?.uploadedFiles || []).filter(hasUploadPayload)
  );
  const [requestKindOverride, setRequestKindOverride] = useState<PatientChatRequestKind | null>(
    null
  );
  const [includeConsentDisclaimer, setIncludeConsentDisclaimer] = useState(false);
  const [isFileDragActive, setIsFileDragActive] = useState(false);
  const [attachmentError, setAttachmentError] = useState("");
  const [isReplyCopied, setIsReplyCopied] = useState(false);
  const [promptError, setPromptError] = useState("");
  const [processingStatus, setProcessingStatus] = useState("");
  const [dictationError, setDictationError] = useState("");
  const [historyError, setHistoryError] = useState("");
  // Session version a local request settled at. A polled "pending" session at or below
  // it is the run this tab already finished, not a newer one.
  const [localSettledSessionVersion, setLocalSettledSessionVersion] = useState<number | null>(null);
  // Read by the session effects. The mutation reports settled in a render that runs
  // before submitPrompt's own updates commit, and that render's state would let a
  // snapshot fetched before the run replace the reply.
  const localSettledSessionVersionRef = useRef<number | null>(null);
  const settleLocalSessionVersion = (version: number | null) => {
    localSettledSessionVersionRef.current = version;
    setLocalSettledSessionVersion(version);
  };

  const fileInputRef = useRef<HTMLInputElement>(null);
  const fileDragDepthRef = useRef(0);
  const promptRef = useRef<HTMLTextAreaElement>(null);
  const promptValueRef = useRef(prompt);
  const dictationSelectionRef = useRef<TextSelection | null>(null);
  const dictationInterimRangeRef = useRef<NoteDictationInterimRange | null>(null);
  const dictationHasTranscriptRef = useRef(false);
  const hasFetchedMacrosRef = useRef(false);
  const sessionVersionRef = useRef(cachedState?.sessionVersion || 0);
  const appliedSessionStampRef = useRef(cachedState?.sessionStamp || null);
  // Every mount re-hydrates: the draft saved on the last unmount is newer than any
  // session this cache absorbed, so the reopen waits for the fresh fetch.
  const sessionHydratedRef = useRef(false);
  const draftPromptAtOpenRef = useRef(cachedState?.draftPrompt || "");
  const hasRefetchedBehindLocalRunRef = useRef(false);
  const latestStateRef = useRef<SmartAssistLocalState>(cachedState || EMPTY_LOCAL_STATE);
  const abortControllerRef = useRef<AbortController | null>(null);
  // After a Stop or failure the server restores the sent message as the draft, which for
  // a quick action is just its label. The composer here is already correct, so the next
  // settled session must not overwrite it.
  const preserveComposerRef = useRef(false);

  const messageMutation = usePatientChatMessage();
  const sessionQuery = usePatientChatSession({ patientId, enabled: Boolean(patientId) });
  const { data: session, isFetching: isFetchingSession, refetch: refetchSession } = sessionQuery;
  const isMessagePending = messageMutation.isPending;
  const isMessagePendingRef = useRef(isMessagePending);
  isMessagePendingRef.current = isMessagePending;
  const saveDraftRef = useRef(saveDraft);
  saveDraftRef.current = saveDraft;

  const isStaleLocalPending =
    localSettledSessionVersion != null && (session?.version ?? 0) <= localSettledSessionVersion;
  const isAwaitingResponse =
    isMessagePending || (session?.status === "pending" && !isStaleLocalPending);

  const macroByDotPhrase = useMemo(() => {
    const result = new Map<string, string>();
    (userMacros || []).forEach((macro) => {
      const dotPhrase = macro?.dot_phrase?.trim();
      const value = macro?.content ?? macro?.value;
      if (dotPhrase && typeof value === "string" && value.trim()) {
        result.set(dotPhrase.toLowerCase(), value);
      }
    });
    // Host phrases (patient-specific) take priority over the saved library.
    if (dotPhraseMacrosByPhrase instanceof Map) {
      dotPhraseMacrosByPhrase.forEach((value, phrase) => result.set(phrase, value));
    }
    return result;
  }, [dotPhraseMacrosByPhrase, userMacros]);

  useEffect(() => {
    if (hasFetchedMacrosRef.current || (userMacros || []).length > 0) return;
    hasFetchedMacrosRef.current = true;
    fetchUserMacros?.();
  }, [fetchUserMacros, userMacros]);

  useEffect(() => {
    if (/\S/.test(prompt) || uploadedFiles.length > 0) setPromptError("");
  }, [prompt, uploadedFiles.length]);

  useEffect(() => {
    const previousPrompt = promptValueRef.current;
    if (previousPrompt === prompt) return;

    const previousInterimRange = dictationInterimRangeRef.current;
    if (previousInterimRange) {
      const rebasedInterimRange = rebaseNoteDictationInterimRange({
        previousValue: previousPrompt,
        nextValue: prompt,
        interimRange: previousInterimRange,
      });
      dictationInterimRangeRef.current = rebasedInterimRange;
      dictationSelectionRef.current = rebasedInterimRange
        ? { start: rebasedInterimRange.end, end: rebasedInterimRange.end }
        : { start: prompt.length, end: prompt.length };
    }

    promptValueRef.current = prompt;
  }, [prompt]);

  const handleDictationTranscript = useCallback(
    (transcript: string, isFinal: boolean, { isSessionStart }: { isSessionStart: boolean }) => {
      const textarea = promptRef.current;
      const selection = dictationSelectionRef.current || {
        start: promptValueRef.current.length,
        end: promptValueRef.current.length,
      };
      const result = insertNoteDictationText({
        currentValue: textarea?.value ?? promptValueRef.current,
        transcript,
        selectionStart: selection.start,
        selectionEnd: selection.end,
        previousInterimRange: dictationInterimRangeRef.current,
        isFinal,
        isSessionStart,
      });

      if (!result) return;

      dictationHasTranscriptRef.current = true;
      dictationInterimRangeRef.current = result.interimRange;
      dictationSelectionRef.current = { start: result.cursorPosition, end: result.cursorPosition };
      promptValueRef.current = result.value;
      setPrompt(result.value);

      requestAnimationFrame(() => {
        const promptTextarea = promptRef.current;
        if (!promptTextarea) return;
        promptTextarea.focus();
        promptTextarea.setSelectionRange(result.cursorPosition, result.cursorPosition);
      });
    },
    []
  );

  const handleDictationConnectionClosed = useCallback(() => {
    const hadTranscript = dictationHasTranscriptRef.current;
    return { reportConnectionError: !hadTranscript, clearStatus: !hadTranscript };
  }, []);

  const {
    isDictating,
    isStarting: isDictationStarting,
    status: dictationStatus,
    stop: stopDictation,
    toggle: toggleDeepgramDictation,
  } = useDeepgramDictation({
    purpose: "ask-knowtex",
    sessionKey: patientId,
    onTranscript: handleDictationTranscript,
    onListening: () => {
      setDictationError("");
      promptRef.current?.focus();
    },
    onStopped: () => {
      dictationInterimRangeRef.current = null;
      dictationSelectionRef.current = null;
      dictationHasTranscriptRef.current = false;
    },
    onConnectionClosed: handleDictationConnectionClosed,
    onError: (message) => setDictationError(message),
    messages: {
      unsupportedMimeType: "This browser does not support microphone dictation for Smart Assist.",
      connectionFailed: "Smart Assist dictation connection failed. Please try again.",
      startFailed: "Failed to start Smart Assist dictation. Please try again.",
    },
    logLabel: "Smart Assist dictation",
  });

  useEffect(() => {
    onDictationStateChange?.({ isDictating, isStarting: isDictationStarting });
  }, [isDictating, isDictationStarting, onDictationStateChange]);

  useEffect(
    () => () => onDictationStateChange?.({ isDictating: false, isStarting: false }),
    [onDictationStateChange]
  );

  useEffect(() => {
    if (isHidden && (isDictating || isDictationStarting)) stopDictation({ clearStatus: true });
  }, [isHidden, isDictating, isDictationStarting, stopDictation]);

  const toggleDictation = useCallback(async () => {
    if (isDictationStarting) return;
    if (isDictating) {
      stopDictation();
      toast.info("Smart Assist dictation stopped.");
      return;
    }
    if (isConflictingDictationActive) {
      setDictationError(conflictingDictationMessage);
      return;
    }

    const textarea = promptRef.current;
    const promptValue = textarea?.value ?? prompt;
    const selection = dictationSelectionRef.current || {
      start: promptValue.length,
      end: promptValue.length,
    };
    const boundedSelection = {
      start: Math.min(selection.start, promptValue.length),
      end: Math.min(selection.end, promptValue.length),
    };
    promptValueRef.current = promptValue;
    dictationSelectionRef.current = boundedSelection;
    dictationInterimRangeRef.current = null;
    dictationHasTranscriptRef.current = false;
    textarea?.focus();
    textarea?.setSelectionRange(boundedSelection.start, boundedSelection.end);
    setDictationError("");
    await toggleDeepgramDictation();
  }, [
    conflictingDictationMessage,
    isConflictingDictationActive,
    isDictating,
    isDictationStarting,
    prompt,
    stopDictation,
    toggleDeepgramDictation,
  ]);

  useLayoutEffect(() => {
    const nextState: SmartAssistLocalState = {
      conversation,
      draftPrompt: prompt,
      latestReply: reply || null,
      latestCitations: citations || {},
      latestRequestKind: requestKind,
      latestRunId,
      latestArtifacts: artifacts,
      promptWorkflowStep,
      activeQuickAction,
      replyOriginQuickActionId,
      uploadedFiles,
    };
    latestStateRef.current = nextState;
    if (stateCacheRef) {
      stateCacheRef.current = {
        ...nextState,
        patientId,
        sessionVersion: sessionVersionRef.current,
        sessionStamp: appliedSessionStampRef.current,
      };
    }
  }, [
    activeQuickAction,
    artifacts,
    citations,
    conversation,
    latestRunId,
    patientId,
    prompt,
    promptWorkflowStep,
    reply,
    replyOriginQuickActionId,
    requestKind,
    stateCacheRef,
    uploadedFiles,
  ]);

  const applySession = useCallback(
    (
      nextSession: PatientChatSessionResponse,
      draftPrompt: string,
      { preserveComposer = false }: { preserveComposer?: boolean } = {}
    ) => {
      setConversation(nextSession.conversation || []);
      if (!preserveComposer) setPrompt(draftPrompt);
      setReply(nextSession.latestReply || "");
      setCitations(nextSession.latestCitations || {});
      setRequestKind(nextSession.latestRequestKind || null);
      setLatestRunId(nextSession.latestRunId || null);
      setArtifacts([]);
      setPromptWorkflowStep(
        nextSession.latestRequestKind === "prompt_reformat" && nextSession.latestReply
          ? PROMPT_WORKFLOW_STEPS.COMPLETE
          : PROMPT_WORKFLOW_STEPS.IDLE
      );
      if (!preserveComposer) {
        setActiveQuickAction(null);
        setRequestKindOverride(null);
      }
      setUploadedFiles((currentFiles) => {
        const availableLocalFiles = currentFiles.filter(hasUploadPayload);
        return availableLocalFiles.length > 0
          ? availableLocalFiles
          : (nextSession.uploadedFiles || []).filter(hasUploadPayload);
      });
      setIsReplyCopied(false);
      sessionVersionRef.current = nextSession.version || 0;
      appliedSessionStampRef.current = getSessionStamp(nextSession);
      markDraftSaved({ patientId, draftPrompt: nextSession.draftPrompt || "" });
    },
    [markDraftSaved, patientId]
  );

  useEffect(() => {
    if (
      !session ||
      isMessagePending ||
      !shouldHydratePatientChatSession({
        isModalOpen: true,
        hasSession: true,
        isFetching: Boolean(isFetchingSession),
        hasPendingWrite,
        isHydrated: sessionHydratedRef.current,
      })
    ) {
      return;
    }

    // A request queued on mount can finish before the first fetch lands, which would
    // leave this snapshot older than the reply already on screen.
    const settledVersion = localSettledSessionVersionRef.current;
    if (settledVersion != null && (session.version ?? 0) < settledVersion) {
      if (!hasRefetchedBehindLocalRunRef.current) {
        hasRefetchedBehindLocalRunRef.current = true;
        void refetchSession?.();
        return;
      }
      sessionHydratedRef.current = true;
      return;
    }

    sessionHydratedRef.current = true;
    if (appliedSessionStampRef.current === getSessionStamp(session)) return;

    const { draftPrompt } = resolvePatientChatDraftOnHydrate({
      serverDraftPrompt: session.draftPrompt,
      serverVersion: session.version,
      lastWrittenVersion: getLastWrittenVersion(patientId, session.sessionId),
      localDraftPrompt: latestStateRef.current.draftPrompt,
      draftPromptAtOpen: draftPromptAtOpenRef.current,
    });
    const preserveComposer = preserveComposerRef.current;
    preserveComposerRef.current = false;
    applySession(session, draftPrompt, { preserveComposer });
    // A preserved composer keeps the local prompt, which may have been edited while loading.
    const promptToPersist = preserveComposer ? latestStateRef.current.draftPrompt : draftPrompt;
    if (promptToPersist !== (session.draftPrompt || "")) {
      saveDraft({ patientId, draftPrompt: promptToPersist });
    }
  }, [
    applySession,
    getLastWrittenVersion,
    hasPendingWrite,
    isFetchingSession,
    isMessagePending,
    localSettledSessionVersion,
    patientId,
    refetchSession,
    saveDraft,
    session,
  ]);

  // Picks up a run that settled on the server after hydration, e.g. one started
  // before the panel opened or a failed request the server rolled back.
  useEffect(() => {
    if (!sessionHydratedRef.current || !session || isMessagePending) return;
    if (appliedSessionStampRef.current === getSessionStamp(session)) return;
    if (session.status !== "completed" && session.status !== "failed") return;
    const settledVersion = localSettledSessionVersionRef.current;
    if (settledVersion != null && (session.version ?? 0) < settledVersion) {
      return;
    }

    if (preserveComposerRef.current) {
      preserveComposerRef.current = false;
      sessionVersionRef.current = session.version || 0;
      appliedSessionStampRef.current = getSessionStamp(session);
      markDraftSaved({ patientId, draftPrompt: session.draftPrompt || "" });
      return;
    }

    applySession(session, session.draftPrompt || "");
    if (session.status === "failed" && session.lastError) {
      setPromptError(SMART_ASSIST_REQUEST_FAILED_MESSAGE);
    }
  }, [applySession, isMessagePending, localSettledSessionVersion, markDraftSaved, patientId, session]);

  useEffect(() => {
    if (!sessionHydratedRef.current || isMessagePending || isDictating || isDictationStarting) {
      return undefined;
    }
    const timeoutId = window.setTimeout(() => {
      saveDraft({ patientId, draftPrompt: latestStateRef.current.draftPrompt });
    }, 800);
    return () => window.clearTimeout(timeoutId);
  }, [isDictating, isDictationStarting, isMessagePending, patientId, prompt, saveDraft]);

  useEffect(
    () => () => {
      // The server ignores draft writes while a message is streaming.
      if (isMessagePendingRef.current) return;
      const { draftPrompt } = latestStateRef.current;
      if (!sessionHydratedRef.current && draftPrompt === draftPromptAtOpenRef.current) return;
      saveDraftRef.current({ patientId, draftPrompt });
    },
    [patientId]
  );

  const resetFileInput = () => {
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const addUploadedFiles = async (selectedFiles: File[]) => {
    if (!selectedFiles.length) return;
    if (uploadedFiles.length + selectedFiles.length > ATTACHMENT_LIMIT) {
      setAttachmentError(SMART_ASSIST_FILE_LIMIT_MESSAGE);
      resetFileInput();
      return;
    }
    setAttachmentError("");

    const sizeError = getAttachmentSizeError(uploadedFiles, selectedFiles);
    if (sizeError) {
      setAttachmentError(sizeError);
      resetFileInput();
      return;
    }

    try {
      const normalizedFiles = await Promise.all(selectedFiles.map(buildUploadedFile));
      setUploadedFiles((previous) => {
        const existing = new Set(previous.map(getUploadedFileKey));
        return [
          ...previous,
          ...normalizedFiles.filter((file) => !existing.has(getUploadedFileKey(file))),
        ];
      });
    } catch (error) {
      setAttachmentError(getErrorMessage(error, "Failed to read uploaded files."));
    } finally {
      resetFileInput();
    }
  };

  const canUploadFiles = Boolean(capabilities?.canUploadObjectiveDataFiles);
  const canAcceptFileDrop =
    canUploadFiles && !isAwaitingResponse && !isDictating && !isDictationStarting;

  const fileDropHandlers = {
    onDragEnter: (event: DragEvent<HTMLElement>) => {
      if (!isFileDragEvent(event)) return;
      event.preventDefault();
      if (!canAcceptFileDrop) return;
      fileDragDepthRef.current += 1;
      setIsFileDragActive(true);
    },
    onDragOver: (event: DragEvent<HTMLElement>) => {
      if (!isFileDragEvent(event)) return;
      event.preventDefault();
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = canAcceptFileDrop ? "copy" : "none";
      }
    },
    onDragLeave: (event: DragEvent<HTMLElement>) => {
      if (fileDragDepthRef.current === 0) return;
      event.preventDefault();
      fileDragDepthRef.current = Math.max(0, fileDragDepthRef.current - 1);
      if (fileDragDepthRef.current === 0) setIsFileDragActive(false);
    },
    onDrop: (event: DragEvent<HTMLElement>) => {
      if (!isFileDragEvent(event)) return;
      event.preventDefault();
      fileDragDepthRef.current = 0;
      setIsFileDragActive(false);
      if (!canAcceptFileDrop) return;
      void addUploadedFiles(Array.from(event.dataTransfer?.files || []));
    },
  };

  const handleFileSelection = (event: ChangeEvent<HTMLInputElement>) => {
    void addUploadedFiles(Array.from(event.target.files || []));
  };

  const removeUploadedFile = (file: PatientChatUploadedFile) => {
    setAttachmentError("");
    setUploadedFiles((previous) => previous.filter((item) => item !== file));
  };

  const handlePromptChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    const nextPrompt = event.target.value;
    const synchronized = syncNoteDictationAfterEdit({
      previousValue: promptValueRef.current,
      nextValue: nextPrompt,
      interimRange: dictationInterimRangeRef.current,
      selectionStart: event.currentTarget.selectionStart,
      selectionEnd: event.currentTarget.selectionEnd,
    });
    dictationInterimRangeRef.current = synchronized.interimRange;
    dictationSelectionRef.current = synchronized.selection;
    promptValueRef.current = nextPrompt;
    setPrompt(nextPrompt);
    if (synchronized.wasInterimRangeInvalidated && isDictating) stopDictation();
  };

  const handlePromptSelect = (event: SyntheticEvent<HTMLTextAreaElement>) => {
    dictationSelectionRef.current = {
      start: event.currentTarget.selectionStart,
      end: event.currentTarget.selectionEnd,
    };
  };

  const handlePromptKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (
      (event.key !== " " && event.key !== "Enter" && event.key !== "Tab") ||
      (event.key === "Enter" && event.shiftKey)
    ) {
      return;
    }
    const textarea = event.currentTarget;
    const { selectionStart, selectionEnd } = textarea;
    if (selectionStart !== selectionEnd) return;

    const currentPrompt = textarea.value || "";
    const match = currentPrompt.slice(0, selectionStart).match(/(?:^|\s)(\.[A-Za-z0-9_-]+)$/);
    if (!match) return;
    const macroValue = macroByDotPhrase.get(match[1].toLowerCase());
    if (!macroValue) return;

    event.preventDefault();
    const insertionStart = selectionStart - match[1].length;
    const delimiter = event.key === "Enter" ? "\n" : event.key === "Tab" ? "\t" : " ";
    const beforeMacro = currentPrompt.slice(0, insertionStart);
    const prefix = beforeMacro.length > 0 && !beforeMacro.endsWith("\n") ? "\n" : "";
    const insertion = `${prefix}${macroValue}${delimiter}`;
    const nextPrompt = beforeMacro + insertion + currentPrompt.slice(selectionEnd);
    const nextCursor = insertionStart + insertion.length;
    setPrompt(nextPrompt);
    requestAnimationFrame(() => {
      promptRef.current?.focus();
      promptRef.current?.setSelectionRange(nextCursor, nextCursor);
    });
  };

  const resolveEffectivePrompt = (sourcePrompt: string): string => {
    if (!activeQuickAction) {
      if (/\S/.test(sourcePrompt)) return sourcePrompt;
      return uploadedFiles.length ? UPLOADED_FILES_FALLBACK_PROMPT : "";
    }
    const trimmedInput = sourcePrompt.trim();
    if (activeQuickAction.needsUserInput && !trimmedInput) return "";
    if (activeQuickAction.buildMessage) return activeQuickAction.buildMessage(sourcePrompt);
    if (activeQuickAction.needsUserInput) return trimmedInput;
    // isOriginatingSmartAssistPromptText matches on this exact "<label>: " format.
    return trimmedInput ? `${activeQuickAction.label}: ${trimmedInput}` : activeQuickAction.label;
  };

  const submitPrompt = async (promptOverride?: string) => {
    if (isDictating || isDictationStarting) {
      setDictationError("Stop dictation before sending Smart Assist.");
      return;
    }
    if (isAwaitingResponse) return;
    const isPromptWorkflow = promptWorkflowStep === PROMPT_WORKFLOW_STEPS.AWAITING_EXAMPLE;
    const effectivePrompt = resolveEffectivePrompt(
      typeof promptOverride === "string" ? promptOverride : prompt
    );
    if (activeQuickAction?.requiresAttachment && !uploadedFiles.length) {
      setPromptError(NO_ATTACHMENT_MESSAGE);
      return;
    }
    if (!effectivePrompt) {
      setPromptError(EMPTY_SMART_ASSIST_PROMPT_MESSAGE);
      return;
    }
    setPromptError("");

    preserveComposerRef.current = false;
    const preSubmitSessionVersion = sessionVersionRef.current;
    let streamedReply = "";
    setReply("");
    setCitations({});
    setRequestKind(null);
    setLatestRunId(null);
    setArtifacts([]);
    setIsReplyCopied(false);
    // The server loads chart context before it opens the stream and sends `start`.
    setProcessingStatus(SMART_ASSIST_READING_CHART_STATUS);
    settleLocalSessionVersion(null);
    if (isPromptWorkflow) setPromptWorkflowStep(PROMPT_WORKFLOW_STEPS.CRAFTING);

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      const response = await messageMutation.mutateAsync({
        patientId,
        body: {
          requestId: window.crypto?.randomUUID?.(),
          requestKind: isPromptWorkflow ? "prompt_reformat" : requestKindOverride || undefined,
          quickAction: !isPromptWorkflow ? activeQuickAction?.quickAction : undefined,
          ...(!isPromptWorkflow && activeQuickAction?.offersConsentToggle && includeConsentDisclaimer
            ? { includeConsentDisclaimer: true }
            : {}),
          message: effectivePrompt,
          ...(!isPromptWorkflow
            ? {
                draftContext: buildDraftContext({
                  latestReply: reply,
                  note,
                  patientSummary,
                  diarizedTranscript,
                  noteCitations,
                  chartHistory,
                  includeFullChartHistory,
                  sendEmptyContext,
                }),
              }
            : {}),
          uploadedFiles: !isPromptWorkflow && uploadedFiles.length ? uploadedFiles : undefined,
        },
        signal: abortController.signal,
        streamHandlers: {
          onStart: (kind) => {
            setRequestKind(kind);
            setProcessingStatus("");
          },
          onProgress: setProcessingStatus,
          onDelta: (text) => {
            setProcessingStatus("");
            streamedReply += text;
            setReply(
              isPromptWorkflow ? extractPromptReformatStreamPreview(streamedReply) : streamedReply
            );
          },
        },
      });

      setConversation((previous) => [
        ...previous,
        { role: "user", content: response.userMessage || effectivePrompt },
        { role: "assistant", content: response.reply },
      ]);
      setReply(response.reply);
      setCitations(response.citations || {});
      setRequestKind(response.requestKind);
      setLatestRunId(response.runId || null);
      sessionVersionRef.current = response.sessionVersion || preSubmitSessionVersion + 1;
      settleLocalSessionVersion(sessionVersionRef.current);
      setArtifacts(response.artifacts || []);
      setPromptWorkflowStep(
        response.requestKind === "prompt_reformat"
          ? PROMPT_WORKFLOW_STEPS.COMPLETE
          : PROMPT_WORKFLOW_STEPS.IDLE
      );
      setPrompt("");
      setRequestKindOverride(null);
      if (activeQuickAction) setReplyOriginQuickActionId(activeQuickAction.id);
      setActiveQuickAction(null);
      setIsReplyCopied(false);
      setProcessingStatus("");
      announce("Smart Assist response ready.");
    } catch (error) {
      setReply("");
      setCitations({});
      setRequestKind(null);
      setLatestRunId(null);
      setArtifacts([]);
      setProcessingStatus("");
      if (isPromptWorkflow) setPromptWorkflowStep(PROMPT_WORKFLOW_STEPS.AWAITING_EXAMPLE);
      // A Stop click intentionally aborts the request. Leave the prompt as-is so the
      // user can fix it and resubmit, with no error noise.
      if ((error as { name?: string } | null)?.name !== "AbortError") {
        console.error("Failed to send Smart Assist message:", error);
        setPromptError(SMART_ASSIST_REQUEST_FAILED_MESSAGE);
      }
      // The server has already committed the failed or cancelled session by now, so
      // refetch instead of letting a stale "pending" snapshot linger until the next poll.
      preserveComposerRef.current = true;
      void refetchSession?.();
    } finally {
      abortControllerRef.current = null;
      settleLocalSessionVersion(
        localSettledSessionVersionRef.current ?? preSubmitSessionVersion + 1
      );
    }
  };

  const submitPromptRef = useRef(submitPrompt);
  submitPromptRef.current = submitPrompt;

  useEffect(() => {
    if (!queuedPrompt || !/\S/.test(queuedPrompt)) return;
    const message = queuedPrompt;
    onQueuedPromptConsumed?.();
    setPrompt(message);
    promptValueRef.current = message;
    void submitPromptRef.current(message);
  }, [queuedPrompt, onQueuedPromptConsumed]);

  const clearHistory = async () => {
    stopDictation({ clearStatus: true });
    setConversation([]);
    setPrompt("");
    setReply("");
    setCitations({});
    setRequestKind(null);
    setRequestKindOverride(null);
    setLatestRunId(null);
    setArtifacts([]);
    setPromptWorkflowStep(PROMPT_WORKFLOW_STEPS.IDLE);
    setActiveQuickAction(null);
    setReplyOriginQuickActionId(null);
    setUploadedFiles([]);
    setIsReplyCopied(false);
    settleLocalSessionVersion(null);
    resetFileInput();
    latestStateRef.current = EMPTY_LOCAL_STATE;
    try {
      await clearSession(patientId);
      // The server deleted the row, so the next write recreates it at version 0.
      sessionVersionRef.current = 0;
      if (stateCacheRef?.current) stateCacheRef.current.sessionVersion = 0;
      setHistoryError("");
      toast.success("Smart Assist chat history cleared.");
    } catch (error) {
      setHistoryError(getErrorMessage(error, "Failed to clear the saved Smart Assist session."));
    }
  };

  const selectQuickAction = (action: SmartAssistQuickAction) => {
    setPromptWorkflowStep(PROMPT_WORKFLOW_STEPS.IDLE);
    setActiveQuickAction(action);
    setPrompt(action.prefillText ?? "");
    setPromptError("");
    setRequestKindOverride(action.requestKind || null);
    setIncludeConsentDisclaimer(false);
    requestAnimationFrame(() => promptRef.current?.focus());
  };

  const startCreatePrompt = () => {
    setActiveQuickAction(null);
    setPrompt("");
    setRequestKindOverride(null);
    setPromptWorkflowStep(PROMPT_WORKFLOW_STEPS.AWAITING_EXAMPLE);
    requestAnimationFrame(() => promptRef.current?.focus());
  };

  const isIntervalHistoryReply =
    requestKind !== "prompt_reformat" &&
    (replyOriginQuickActionId
      ? replyOriginQuickActionId === "create_interval_history"
      : conversationHasIntervalHistoryDraft(conversation));

  const lastConversationMessage = conversation[conversation.length - 1];
  const displayedConversation =
    reply &&
    lastConversationMessage?.role === "assistant" &&
    lastConversationMessage?.content === reply
      ? conversation.slice(0, -1)
      : conversation;
  const summary = artifacts.find(
    (artifact) => artifact?.type === "prompt_reformat_summary"
  )?.summary;

  return {
    canUploadFiles,

    prompt,
    promptRef,
    handlePromptChange,
    handlePromptSelect,
    handlePromptKeyDown,
    promptError,
    processingStatus,

    conversation,
    displayedConversation,
    reply,
    citations,
    requestKind,
    promptReformatSummary: typeof summary === "string" ? summary : undefined,
    isIntervalHistoryReply,
    isReplyCopied,
    markReplyCopied: () => setIsReplyCopied(true),

    promptWorkflowStep,
    activeQuickAction,
    shouldShowPromptWorkflowStatus:
      promptWorkflowStep === PROMPT_WORKFLOW_STEPS.AWAITING_EXAMPLE ||
      (promptWorkflowStep === PROMPT_WORKFLOW_STEPS.CRAFTING && !reply) ||
      (Boolean(activeQuickAction) && !reply),
    selectQuickAction,
    startCreatePrompt,
    includeConsentDisclaimer,
    setIncludeConsentDisclaimer,

    isAwaitingResponse,
    // Text is already streaming into the reply once a local request has a reply.
    pendingStatus:
      isAwaitingResponse && !(isMessagePending && reply)
        ? processingStatus || SMART_ASSIST_THINKING_STATUS
        : null,
    submit: () => void submitPrompt(),
    stop: () => abortControllerRef.current?.abort(),

    isDictating,
    isDictationStarting,
    dictationStatus,
    dictationError,
    toggleDictation,

    uploadedFiles,
    attachmentError,
    fileInputRef,
    handleFileSelection,
    removeUploadedFile,
    isFileDragActive,
    fileDropHandlers,

    canClearHistory: !(
      isAwaitingResponse ||
      isDictationStarting ||
      (!conversation.length &&
        !reply &&
        !prompt &&
        !isDictating &&
        !uploadedFiles.length &&
        promptWorkflowStep === PROMPT_WORKFLOW_STEPS.IDLE &&
        !activeQuickAction)
    ),
    clearHistory,
    historyError,
  };
}
